/**
 * The lookahead read — the last collection on the project screen that still
 * reported a failure to read as a fact about the schedule.
 *
 * F-CORE-PROJECT-JOURNEY-1 closed this for the project list, the work
 * breakdown and the activity feed. The lookahead was missed, and it is the
 * one a superintendent looks at every morning:
 *
 *   "Ready to start now  0"
 *
 * meant three different things and rendered as one glyph — nothing is ready,
 * the rollup was not sent, or the payload could not be read at all. Only the
 * first is a fact about the project.
 *
 * This module OWNS NO VOCABULARY OF ITS OWN. The three read states come from
 * the journey presenter and the {established, value} fact shape comes from
 * the command presenter, because a second copy of an answer is the defect
 * those two modules exist to remove — see F-4 in the census, still open.
 * What is new here is only the shape of THIS payload: an object carrying a
 * `rollup` of figures and an `items` array, either of which can be absent or
 * unreadable independently of the other.
 */
import {
  REFUSED,
  UNREADABLE,
  READ,
  readCollection,
  NOT_ESTABLISHED_LABEL,
} from '../journey/journeyPresentation';
import { numberFact, NOT_ESTABLISHED } from '../command/commandPresentation';

export { REFUSED, UNREADABLE, READ, NOT_ESTABLISHED_LABEL };

/**
 * The four figures the backend's rollup carries. Named here so that a field
 * the backend stops sending becomes NOT ESTABLISHED rather than silently
 * vanishing from the screen.
 */
export const ROLLUP_FIELDS = Object.freeze(['ready', 'blocked', 'in_progress', 'undated']);

/** A lookahead row is readable when it is an object we can render at all. */
export const lookaheadRowIsReadable = (row) =>
  Boolean(row) && typeof row === 'object' && !Array.isArray(row);

const allNotEstablished = () => {
  const rollup = {};
  for (const field of ROLLUP_FIELDS) rollup[field] = NOT_ESTABLISHED;
  rollup.total = NOT_ESTABLISHED;
  return rollup;
};

/**
 * Read the rollup.
 *
 * An ABSENT rollup is not a rollup of zeros. `{ready: 0}` is a measurement —
 * the crew has nothing ready this morning — and the screen must be able to
 * say that without also saying it whenever the backend simply did not send
 * the object.
 *
 * `total` is read from the payload when the backend states it and is NOT
 * derived by summing the other three: they are windowed counts and a sum of
 * them is a number nobody computed.
 */
export const readRollup = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return allNotEstablished();
  const rollup = {};
  for (const field of ROLLUP_FIELDS) rollup[field] = numberFact(raw[field]);
  rollup.total = numberFact(raw.total);
  return rollup;
};

/**
 * Read a whole lookahead payload.
 *
 * `requestFailed` is passed BY THE CALLER rather than inferred from a null
 * payload, for the same reason it is in the journey presenter: a route that
 * legitimately answers null and a route that blew up are different things,
 * and guessing between them is the original defect in miniature.
 */
export const readLookahead = (payload, { requestFailed = false } = {}) => {
  if (requestFailed) {
    return {
      state: REFUSED,
      rollup: allNotEstablished(),
      items: [],
      unreadableRows: 0,
      reason: 'The lookahead did not load, so what is ready and what is blocked is unknown.',
    };
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {
      state: UNREADABLE,
      rollup: allNotEstablished(),
      items: [],
      unreadableRows: 0,
      reason: 'The lookahead response was not an object. MALFORMED IS NOT EMPTY.',
    };
  }

  // The rows and the figures fail independently. A backend that sends a good
  // rollup and a broken items array should still be able to tell the
  // superintendent how many activities are blocked.
  const rows = readCollection(payload.items, { rowIsReadable: lookaheadRowIsReadable });
  const rollup = readRollup(payload.rollup);

  if (rows.state === UNREADABLE) {
    return {
      state: READ,
      rollup,
      items: [],
      unreadableRows: 0,
      itemsUnreadable: true,
      reason: 'The lookahead activities were not a list. MALFORMED IS NOT EMPTY.',
    };
  }
  return {
    state: READ,
    rollup,
    items: rows.items,
    unreadableRows: rows.unreadableRows,
    itemsUnreadable: false,
    reason: null,
  };
};

/**
 * The label for a single rollup figure. A figure nobody stated never renders
 * as a digit — that is the whole point of the module.
 */
export const rollupLabel = (fact) =>
  fact && fact.established ? String(fact.value) : NOT_ESTABLISHED_LABEL;

/**
 * The tab count. `Lookahead (0)` on a read that never answered was the
 * F-8 defect; this returns null when there is no established total so the
 * caller can render an em dash rather than a number.
 */
export const lookaheadCountLabel = (read) => {
  if (!read || read.state !== READ) return null;
  return read.rollup.total.established ? String(read.rollup.total.value) : null;
};

/**
 * Tone for a figure. A figure that is NOT ESTABLISHED is never coloured as
 * good news: "0 blocked" is green, "we do not know how many are blocked" is
 * not, and colour is the fastest way to tell a superintendent a lie.
 */
export const rollupTone = (fact, { zeroIs = 'neutral', nonZeroIs = 'neutral' } = {}) => {
  if (!fact || !fact.established) return 'unknown';
  return fact.value === 0 ? zeroIs : nonZeroIs;
};

/**
 * What the items list should say. Kept separate from the figures because a
 * readable rollup alongside an unreadable items array is a real state the
 * backend can produce, and the screen has to hold both at once.
 */
export const lookaheadItemsView = (read) => {
  // A read that has not happened yet is not a read that failed. The caller
  // holds null while the request is in flight, and saying "could not be read"
  // there would be its own small lie — the same one, one state earlier.
  if (!read) {
    return {
      kind: 'LOADING',
      heading: 'Reading the lookahead…',
      body: 'The next 14 days have not been read yet.',
      retryable: false,
    };
  }
  if (read.state === REFUSED) {
    return {
      kind: 'REFUSED',
      heading: 'The lookahead could not be read',
      body:
        read.reason ||
        'The lookahead did not load, so what is ready and what is blocked is unknown.',
      retryable: true,
    };
  }
  if (read.state === UNREADABLE) {
    return {
      kind: 'UNREADABLE',
      heading: 'The lookahead could not be read',
      body: read.reason,
      retryable: true,
    };
  }
  if (read.itemsUnreadable) {
    return {
      kind: 'UNREADABLE',
      heading: 'The lookahead activities could not be read',
      body: read.reason,
      retryable: true,
    };
  }
  if (read.items.length === 0) {
    return {
      kind: 'EMPTY',
      heading: 'Nothing is scheduled in the lookahead window',
      body: 'The lookahead was read and it lists no activities in the next 14 days.',
      retryable: false,
    };
  }
  return { kind: 'POPULATED', heading: null, body: null, retryable: false };
};
